import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { environment } from '../../environments/environment';
import { CustomXmlPart } from '../interfaces/customxmlpart';
import { AddInInfo, ApplicationStoreService, INamespaceSettings } from './application-store.service';
import { LoggingService } from './logging.service';
import { WordService } from './word.service';

interface UpgradeExchange {
  wep: any;
  'model-part': string;
  'dope-part': string;
}

@Injectable({ providedIn: 'root' })
export class UpgradeService {
  constructor(
    private httpClient: HttpClient,
    private applicationStoreService: ApplicationStoreService,
    private wordService: WordService,
    private loggingService: LoggingService
  ) {}

  async run(): Promise<void> {
    return new Promise(async (resolve, reject) => {
      const nss = this.tryGetNamespaceSettings(resolve, reject);
      if (!nss) {
        return;
      }

      try {
        const addInInfo = this.applicationStoreService.getAddInInfo();

        const modelXmlPart = await this.wordService.getCustomXmlPart(nss.modelId);
        const modelXml = await modelXmlPart.getXml();

        const dopeXmlPart = await this.wordService.getCustomXmlPart(nss.dopeXPathsId);
        const dopeXml = await dopeXmlPart.getXml();

        const upgradeExchange = this.createRequest(addInInfo, nss, modelXml, dopeXml);

        this.callServerUpgrade(upgradeExchange, modelXmlPart, dopeXmlPart, resolve, reject);
      } catch (error) {
        this.loggingService.error('UpgradeService', 'run', error);
        reject(error);
      }
    });
  }

  async getLatestWordAddinVersion(): Promise<string | null | undefined> {
    return this.httpClient
      .get(window.location.origin + `/manifest/mangular-manifest-${environment.name}.xml`, { responseType: 'text' })
      .pipe(
        map((response: string) => {
          const parser = new DOMParser();
          const xmlDoc = parser.parseFromString(response, 'application/xml');
          const versionElement = xmlDoc.getElementsByTagName('Version')[0];
          return versionElement ? versionElement.textContent : null;
        }),
        catchError((error) => {
          console.error('Failed to fetch the manifest file', error);
          return of(null);
        })
      )
      .toPromise();
  }

  private tryGetNamespaceSettings(
    resolve: (value: void | PromiseLike<void>) => void,
    reject: (reason?: any) => void
  ): INamespaceSettings | undefined {
    const namespaces = this.applicationStoreService.getNamespaces();
    if (namespaces.length === 0) {
      console.debug('No namespaces in document. No server upgrade required.');
      resolve();
      return undefined;
    }

    if (namespaces.length > 1) {
      reject('More than one namespace is not supported by the server upgrade process.');
      return undefined;
    }

    const nss: INamespaceSettings | null = this.applicationStoreService.getNamespaceSettings(namespaces[0]);
    if (nss === null) {
      reject(`Server upgrade process could not retrieve namespace settings for '${namespaces[0]}'.`);
      return undefined;
    }

    return nss;
  }

  private createRequest(addInInfo: AddInInfo, nss: INamespaceSettings, modelXml: string, dopeXml: string): UpgradeExchange {
    return {
      wep: {
        'kp-add-in-info': JSON.stringify(addInInfo),
        'kp-ns-parts': JSON.stringify(nss ? [nss] : []),
      },
      'model-part': this.encodeUtf8Base64(modelXml),
      'dope-part': this.encodeUtf8Base64(dopeXml),
    };
  }

  private callServerUpgrade(
    upgradeExchange: UpgradeExchange,
    modelXmlPart: CustomXmlPart,
    dopeXmlPart: CustomXmlPart,
    resolve: (value: void | PromiseLike<void>) => void,
    reject: (reason?: any) => void
  ): void {
    this.httpClient.post<any>(`${environment.kpHost}/upgrade/run`, upgradeExchange).subscribe({
      next: (response: { modified: boolean; result: UpgradeExchange }) => {
        if (!response.modified) {
          this.loggingService.log('UpgradeService', 'callServerUpgrade', 'Server upgrade: No changes.');
          resolve();
          return;
        }

        this.loggingService.log(
          'UpgradeService',
          'callServerUpgrade',
          `Upgraded from ${JSON.parse(upgradeExchange.wep['kp-add-in-info']).currentVersion} ` +
            `to ${response.result.wep['kp-add-in-info'].currentVersion}`
        );

        try {
          this.updateWordDocument(response.result, modelXmlPart, dopeXmlPart);
          resolve();
        } catch (err: any) {
          reject(err);
        }
      },
      error: (err) => {
        reject(err);
      },
    });
  }

  private updateWordDocument(result: UpgradeExchange, modelXmlPart: CustomXmlPart, dopeXmlPart: CustomXmlPart): void {
    Word.run(async (context) => {
      // NOTE: we don't handle adding parts

      const modelXml = this.decodeUtf8Base64(result['model-part']);
      await modelXmlPart.setXml(modelXml);

      const dopeXml = this.decodeUtf8Base64(result['dope-part']);
      await dopeXmlPart.setXml(dopeXml);

      await context.sync();
    });
  }

  private encodeUtf8Base64(text: string): string {
    const bytes = new TextEncoder().encode(text);
    const binString = Array.from(bytes, (byte) => String.fromCodePoint(byte)).join('');
    return btoa(binString);
  }

  private decodeUtf8Base64(utf8Base64: string): string {
    const binString = atob(utf8Base64);
    const bytes = Uint8Array.from(binString, (char) => char.charCodeAt(0));
    return new TextDecoder('utf-8').decode(bytes);
  }
}
